/**
 * Crop frame sizing for EntityImageEditor.
 *
 * The frame always refits the visible canvas so it keeps its margins to the edges.
 * Keeping the photo framed identically across resizes is handled in
 * `entityImageCropView.js`, which re-derives zoom and translation from the crop area.
 */

/** Longer edge of the exported "full" crop buffer (before backend derivatives). */
export const ENTITY_IMAGE_FULL_MAX_EDGE = 1600;

/** Fraction of the canvas reserved as margin on each side (at least minMarginPx). */
export const CROP_FRAME_MARGIN_RATIO = 0.06;
export const CROP_FRAME_MIN_MARGIN_PX = 16;

/**
 * Compute a crop frame that fits inside the canvas with a minimum edge margin.
 * Size depends only on canvas + aspect — not on the source photo.
 *
 * @param {number} containerWidth
 * @param {number} containerHeight
 * @param {number} aspect width/height
 * @param {{ marginRatio?: number, minMarginPx?: number }} [options]
 * @returns {{ width: number, height: number } | null}
 */
export function computeInsetCropSize(
  containerWidth,
  containerHeight,
  aspect,
  { marginRatio = CROP_FRAME_MARGIN_RATIO, minMarginPx = CROP_FRAME_MIN_MARGIN_PX } = {},
) {
  if (!containerWidth || !containerHeight || !aspect || aspect <= 0) {
    return null;
  }

  const marginX = Math.max(minMarginPx, containerWidth * marginRatio);
  const marginY = Math.max(minMarginPx, containerHeight * marginRatio);
  const availableW = Math.max(1, containerWidth - 2 * marginX);
  const availableH = Math.max(1, containerHeight - 2 * marginY);

  let width;
  let height;
  if (availableW / availableH > aspect) {
    height = availableH;
    width = height * aspect;
  } else {
    width = availableW;
    height = width / aspect;
  }

  // Keep sub-pixel precision: rounding each edge independently would distort the
  // aspect and leak into the exported crop.
  return {
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}

/**
 * Target pixel size for the exported full crop (longer edge = ENTITY_IMAGE_FULL_MAX_EDGE).
 * @param {number} aspect
 * @param {number} [maxEdge]
 * @returns {{ width: number, height: number }}
 */
export function resolveFullOutputSize(aspect, maxEdge = ENTITY_IMAGE_FULL_MAX_EDGE) {
  const safeAspect = aspect > 0 ? aspect : 1;
  if (safeAspect >= 1) {
    return {
      width: maxEdge,
      height: Math.max(1, Math.round(maxEdge / safeAspect)),
    };
  }
  return {
    width: Math.max(1, Math.round(maxEdge * safeAspect)),
    height: maxEdge,
  };
}
